/**
 * ⟦C52 · A-170 §1⟧ **L'écriture, un jeu de mots qui tombent** : le noyau PUR.
 *
 * Matt, 01/10/2026 : « un truc à la Tetris ou Mario pilule : des mots qui tombent et il faut les
 * mettre au bon endroit. On ne cherche pas à éliminer les lignes, on cherche à mettre les mots au
 * bon endroit. Et ça va de plus en plus vite. » Des **blocs-phrases** tombent un par un dans un
 * plateau de colonnes (Accroche, Histoire, Chute, Appel) ; le joueur pousse chaque bloc dans la
 * colonne à laquelle sa phrase appartient. Plus tard : des **pièges** (« pub douteuse ») à laisser
 * filer, et des **doublets** (deux blocs à l'écran).
 *
 * ## Le contrat, et ce que ce module porte
 *
 * 1. **Le serveur génère** la suite de blocs (`genererBlocs`, seedée), leurs colonnes attendues,
 *    leurs instants d'apparition et leurs vitesses de chute. Le client ne tire rien.
 * 2. **Le serveur note** à partir du **journal de placements** que le client renvoie
 *    (`noterBlocs`), après avoir vérifié sa plausibilité (`verifierPlacements`) : un bloc ne peut
 *    être posé qu'entre le plus tôt que sa chute rapide permet et l'instant où il touche le sol.
 * 3. **Un seul algorithme.** Générateur, barème et plausibilité vivent ICI, importés par le serveur
 *    (autoritaire) comme par le bac à sable du client : le score affiché et le score noté ne
 *    peuvent pas dériver, il n'y a qu'une fonction.
 *
 * Tout est **pur** : aucune I/O, aucune horloge, pas de `Math.random`. L'aléa est un `RngBlocs`
 * injecté (le RNG de pattern du serveur, ou `draw` du moteur dans le bac à sable).
 *
 * ## Le journal : ce qu'est un « placement »
 *
 * Une entrée par bloc **touché** par le joueur (déplacé, ou lâché) : `{ bloc, colonne, atMs }`,
 * où `atMs` est l'instant où le bloc se pose (il touche le sol) et `colonne` la colonne où il s'est
 * posé. Un bloc que le joueur n'a jamais touché n'a pas d'entrée : il est tombé là où il est
 * apparu. C'est ce qui rend les pièges possibles (laisser filer = ne rien faire), et c'est pourquoi
 * le générateur ne fait JAMAIS apparaître un bloc ordinaire dans sa bonne colonne (sinon ne rien
 * faire rapporterait des points), sauf quand un assist l'offre (`offert`).
 */
// ─────────────────────────────────────────────────────────────────────────────
// Les colonnes, les phrases, les constantes du jeu
// ─────────────────────────────────────────────────────────────────────────────
/** Les quatre parties d'une vidéo, dans l'ordre d'apparition des colonnes (3 au début, 4 plus tard). */
export const COLONNES = ["accroche", "histoire", "chute", "appel"];
/** Le libellé de chaque colonne, tel qu'il s'affiche au pied du plateau. */
export const LIBELLE_COLONNE = {
    accroche: "Accroche",
    histoire: "Histoire",
    chute: "Chute",
    appel: "Appel",
};
/**
 * **La chute rapide** (↓, ou un second appui sur la colonne) : le bloc tombe ×4 plus vite. Le serveur
 * s'en sert pour borner le plus tôt qu'un bloc peut se poser (`verifierPlacements`) : c'est la même
 * constante des deux côtés, donc la borne et le geste ne peuvent pas diverger.
 */
export const CHUTE_RAPIDE = 4;
/** Marge (ms) au-delà de l'instant où un bloc touche le sol : un geste tardif d'une frame n'est pas une triche. */
export const PLACEMENT_MARGE_HAUT_MS = 250;
/** Marge (ms) sous le plus tôt que la chute rapide permet : l'arrondi d'un horodatage. */
export const PLACEMENT_MARGE_BAS_MS = 60;
/**
 * ⟦C56 · A-173⟧ **Le plancher de la chute** : sous cette durée (ms), une phrase de trois lignes n'a plus
 * le temps d'être lue, ni le bloc le temps d'être poussé de deux colonnes : ce n'est plus un jeu
 * d'adresse. La montée de la manche s'arrête là, quel que soit le palier. La cohérence du contenu
 * (`server/src/atelier/contenu.ts`) refuse une table dont la chute finale passerait dessous, et un
 * test prouve que chaque bloc généré le respecte.
 */
export const CHUTE_LISIBLE_MIN_MS = 1_000;
/**
 * ⟦C56 · A-173⟧ **La montée minimale** d'une manche : le dernier bloc tombe au moins ce nombre de fois
 * plus vite que le premier. La cohérence du contenu l'exige de chaque palier (`chute_depart_ms` ÷
 * `chute_fin_ms`), un test le prouve sur les blocs réellement générés.
 */
export const RAPPORT_MONTEE_MIN = 3;
/** Le plateau reste vide ce temps-là au début de la manche : le joueur lit les colonnes. */
export const ATTENTE_DEBUT_MS = 1000;
/** Le plateau reste affiché ce temps-là après la chute du dernier bloc. */
export const FIN_DE_MANCHE_MS = 600;
/** Un doublet : le second bloc apparaît quand le premier a fait ce quotient de sa chute (plancher en ms). */
export const DOUBLET_DECALAGE = { quotient: 0.42, plancherMs: 700 };
/** Les deux premiers événements d'une manche sont toujours des blocs ordinaires (l'échauffement). */
export const EVENEMENTS_ECHAUFFEMENT = 2;
/** Les pièges et les doublets se posent dans les premiers 85 % de la manche (en TEMPS), jamais en toute fin. */
export const PART_UTILE_DE_LA_MANCHE = 0.85;
const borne = (v, min, max) => Math.max(min, Math.min(max, v));
/** Mélange de Fisher-Yates sur le RNG injecté. */
function melanger(items, rng) {
    const sortie = [...items];
    for (let i = sortie.length - 1; i > 0; i--) {
        const j = Math.min(i, Math.floor(rng.next() * (i + 1)));
        [sortie[i], sortie[j]] = [sortie[j], sortie[i]];
    }
    return sortie;
}
/**
 * ⟦C56 · A-173⟧ **La courbe de la manche** : la durée de chute d'un bloc qui apparaît à l'instant `t`, et
 * le souffle qui suit un bloc tombé à l'instant `t`. Une seule fonction, que `genererBlocs` appelle et
 * que les tests interrogent : la forme de la montée se prouve sur la courbe elle-même, pas sur les
 * quelques blocs qu'une manche en tire.
 *
 * Avec `w = avancement^courbe` (0 à l'ouverture, 1 quand le dernier bloc possible apparaît) :
 *
 *  - la **vitesse** de chute est `1 + (R - 1) × w` fois celle du départ, `R = chuteDepartMs / chuteFinMs` ;
 *    la durée de chute passe donc de `chuteDepartMs` à `chuteFinMs`, et le dernier bloc tombe `R` fois
 *    plus vite que le premier ;
 *  - le **souffle** va de `pauseMs` à `pauseFinMs` en ligne droite en `w`.
 *
 * Avec `courbe` ≥ 2, `w` reste petit tant que la manche n'a pas passé ses deux tiers : le début est
 * calme, c'est le dernier tiers qui s'emballe.
 */
export function courbeDeLaManche(params) {
    const attente = params.attenteMs ?? ATTENTE_DEBUT_MS;
    const chuteFin = Math.max(1, params.chuteFinMs);
    const rapport = Math.max(1, params.chuteDepartMs) / chuteFin;
    const exposant = Math.max(1, params.courbe);
    // L'avancement atteint 1 quand le dernier bloc possible apparaît : sa chute, le souffle et la chute
    // du suivant (qui ne tiendrait plus) sont retirés de l'horizon, d'où `2 × chuteFin + pauseFin`.
    const horizon = Math.max(1, params.budgetMs - attente - 2 * chuteFin - params.pauseFinMs);
    const montee = (t) => Math.pow(borne((t - attente) / horizon, 0, 1), exposant);
    return {
        chute: (t) => Math.max(1, Math.round(params.chuteDepartMs / (1 + (rapport - 1) * montee(t)))),
        souffle: (t) => Math.max(0, Math.round(params.pauseMs + (params.pauseFinMs - params.pauseMs) * montee(t))),
    };
}
/**
 * **La suite de blocs de la manche.** Déterministe : mêmes `params`, mêmes `phrases`, même RNG ⇒ même
 * suite, à l'octet près (c'est le garde anti-re-roll : le serveur la régénère au scoring).
 *
 * Le déroulé, événement après événement :
 *
 *  - un **événement** est un bloc ordinaire, un bloc piège, ou un doublet (deux blocs) ;
 *  - le premier apparaît après `ATTENTE_DEBUT_MS`, chacun des suivants un souffle après la chute du
 *    précédent : **un par un**, jamais deux à l'écran hors doublet ;
 *  - ⟦C56⟧ **la vitesse monte sur une courbe** : avec `w = avancement^courbe` (0 au début de la manche,
 *    1 quand le dernier bloc apparaît), la vitesse de chute est `1 + (R - 1) × w` fois la vitesse de
 *    départ, d'où une durée de chute de `chuteDepartMs` à `chuteFinMs`, et le souffle entre deux blocs
 *    va de `pauseMs` à `pauseFinMs` en ligne droite en `w` : le début est calme, le dernier tiers
 *    frénétique, et plus le jeu avance, moins on a le temps de souffler ;
 *  - la manche s'arrête quand l'événement suivant ne tiendrait plus dans `budgetMs`.
 *
 * Les colonnes sortent d'un **sac** (chaque colonne une fois avant qu'aucune ne revienne) : un joueur
 * ne voit pas six « Chute » de suite, et chaque colonne sert à peu près autant.
 */
export function genererBlocs(params, phrases, rng) {
    const cols = params.colonnes;
    const nCols = cols.length;
    const budget = params.budgetMs;
    const attente = params.attenteMs ?? ATTENTE_DEBUT_MS;
    const entier = (max) => Math.min(max - 1, Math.floor(rng.next() * max));
    // 0. La courbe : durée de chute et souffle à un instant de la manche (⟦C56⟧)
    const { chute: duree, souffle } = courbeDeLaManche(params);
    // — 1. Où tombent les pièges et les doublets : des numéros d'événement, tirés d'avance —
    //    Combien d'événements la manche tient-elle, et quand ? On déroule la chronologie à blanc (sans
    //    pièges ni doublets) : la courbe n'est pas linéaire, les derniers événements se serrent, et ni une
    //    moyenne des deux chutes ni un rang en pourcentage ne disent plus où est « la fin de la manche ».
    const limiteUtile = attente + PART_UTILE_DE_LA_MANCHE * (budget - attente);
    let dernierPossible = -1;
    for (let t = attente, e = 0;; e++) {
        const chute = duree(t);
        if (t + chute > budget)
            break;
        if (t <= limiteUtile)
            dernierPossible = e;
        t += chute + souffle(t + chute);
    }
    const candidats = [];
    for (let e = EVENEMENTS_ECHAUFFEMENT; e <= dernierPossible; e++)
        candidats.push(e);
    const tires = melanger(candidats, rng);
    const pieges = new Set();
    const doublets = new Set();
    for (const e of tires) {
        if (pieges.size >= params.pieges)
            break;
        // Jamais deux pièges collés : une pub de plus ne se lit plus comme un piège, mais comme un rythme.
        if (pieges.has(e - 1) || pieges.has(e + 1))
            continue;
        pieges.add(e);
    }
    for (const e of tires) {
        if (doublets.size >= params.doublets)
            break;
        if (!pieges.has(e) && !doublets.has(e))
            doublets.add(e);
    }
    // — 2. Le sac de colonnes et les réserves de phrases —
    let sac = [];
    let derniere = null;
    const prochaineColonne = (exclure) => {
        const aEviter = (c) => nCols > 1 && c === exclure;
        if (sac.length === 0) {
            sac = melanger(cols, rng);
            // Une colonne ne sort jamais deux fois d'affilée à la frontière de deux sacs.
            if (nCols > 1 && sac[sac.length - 1] === derniere) {
                [sac[0], sac[sac.length - 1]] = [sac[sac.length - 1], sac[0]];
            }
        }
        let idx = sac.length - 1;
        while (idx >= 0 && aEviter(sac[idx]))
            idx--;
        if (idx < 0) {
            // Il ne reste que la colonne à éviter : un nouveau sac passe devant, le reste attend.
            sac = [...sac, ...melanger(cols, rng)];
            idx = sac.length - 1;
            while (idx >= 0 && aEviter(sac[idx]))
                idx--;
        }
        const [colonne] = sac.splice(idx, 1);
        derniere = colonne;
        return colonne;
    };
    const reserves = new Map();
    const piocher = (cle) => {
        let r = reserves.get(cle);
        if (!r || r.length === 0) {
            r = melanger(cle === "pieges" ? phrases.pieges : phrases[cle], rng);
            reserves.set(cle, r);
        }
        return r.pop();
    };
    /** La voie d'apparition : n'importe laquelle sauf `interdites` (la bonne colonne, ou celle du bloc jumeau). */
    const tirerVoie = (interdites) => {
        const libres = [];
        for (let v = 0; v < nCols; v++)
            if (!interdites.includes(v))
                libres.push(v);
        return libres.length > 0 ? libres[entier(libres.length)] : entier(nCols);
    };
    // — 3. La chronologie —
    const blocs = [];
    let t = attente;
    for (let e = 0;; e++) {
        const chuteMs = duree(t);
        // Un doublet ou un piège que les doublets précédents ont repoussé passé la part utile redevient un
        // bloc ordinaire : ni l'un ni l'autre ne tombent « en toute fin » de manche, jamais.
        const tropTard = t > limiteUtile;
        const double = doublets.has(e) && !tropTard;
        const decalage = double
            ? Math.max(DOUBLET_DECALAGE.plancherMs, Math.round(chuteMs * DOUBLET_DECALAGE.quotient))
            : 0;
        const finEvenement = t + decalage + chuteMs;
        if (finEvenement > budget)
            break;
        if (pieges.has(e) && !tropTard) {
            blocs.push({
                i: blocs.length,
                texte: piocher("pieges"),
                colonne: null,
                apparitionMs: t,
                chuteMs,
                voie: entier(nCols),
            });
        }
        else {
            const premier = e === 0 && params.premierOffert && cols.includes("accroche");
            const colonneA = premier ? "accroche" : prochaineColonne();
            const voieBonneA = cols.indexOf(colonneA);
            const a = {
                i: blocs.length,
                texte: piocher(colonneA),
                colonne: colonneA,
                apparitionMs: t,
                chuteMs,
                voie: premier ? voieBonneA : tirerVoie([voieBonneA]),
            };
            if (premier)
                a.offert = true;
            blocs.push(a);
            if (double) {
                const colonneB = prochaineColonne(colonneA);
                const voieBonneB = cols.indexOf(colonneB);
                blocs.push({
                    i: blocs.length,
                    texte: piocher(colonneB),
                    colonne: colonneB,
                    apparitionMs: t + decalage,
                    chuteMs,
                    voie: tirerVoie([voieBonneB, a.voie]),
                });
            }
        }
        t = finEvenement + souffle(finEvenement);
    }
    // — 4. *Storytime* : les N premiers blocs Histoire arrivent déjà rangés —
    let restantes = Math.max(0, Math.trunc(params.histoiresOffertes));
    for (const b of blocs) {
        if (restantes === 0)
            break;
        if (b.colonne === "histoire" && !b.offert) {
            b.voie = cols.indexOf("histoire");
            b.offert = true;
            restantes--;
        }
    }
    return blocs;
}
/** L'instant où un bloc touche le sol à vitesse normale. */
export function atterrissageMs(b) {
    return b.apparitionMs + b.chuteMs;
}
/** La durée de la manche : le dernier bloc a touché le sol, plus `FIN_DE_MANCHE_MS`. */
export function dureeDeLaManche(blocs) {
    let fin = 0;
    for (const b of blocs)
        fin = Math.max(fin, atterrissageMs(b));
    return fin + FIN_DE_MANCHE_MS;
}
/**
 * Combien de blocs pièges, et combien de **doublets** (un bloc qui apparaît avant que le précédent
 * ait touché le sol), la suite porte réellement : le générateur en pose « au plus » autant que
 * demandé, l'intro de la manche annonce ce qui est vraiment là.
 */
export function compterPiegesEtDoublets(blocs) {
    let pieges = 0;
    let doublets = 0;
    blocs.forEach((b, i) => {
        if (b.colonne === null)
            pieges++;
        const avant = blocs[i - 1];
        if (avant && atterrissageMs(avant) > b.apparitionMs)
            doublets++;
    });
    return { pieges, doublets };
}
/**
 * **La plausibilité du journal** (SPEC-API §6.4, pas 2). Rend la raison d'un geste impossible, ou
 * `null`. Un bloc ne peut se poser :
 *
 *  - que s'il existe (`bloc_inconnu`) et une seule fois (`bloc_pose_deux_fois`) ;
 *  - que dans une colonne du plateau (`colonne_inconnue`) ;
 *  - **pas avant** que sa chute rapide l'ait amené au sol (`pose_trop_tot`) : un placement à
 *    l'instant même de l'apparition n'est pas un geste ;
 *  - **pas après** qu'il a touché le sol à sa vitesse (`pose_apres_le_sol`) : passé cet instant, le
 *    bloc est posé, on ne le déplace plus.
 */
export function verifierPlacements(blocs, colonnes, placements) {
    const vus = new Set();
    for (const p of placements) {
        if (!Number.isInteger(p.bloc) || !blocs[p.bloc])
            return "bloc_inconnu";
        const b = blocs[p.bloc];
        if (!colonnes.includes(p.colonne))
            return "colonne_inconnue";
        if (vus.has(p.bloc))
            return "bloc_pose_deux_fois";
        vus.add(p.bloc);
        const plusTot = b.apparitionMs + b.chuteMs / CHUTE_RAPIDE - PLACEMENT_MARGE_BAS_MS;
        if (p.atMs < plusTot)
            return "pose_trop_tot";
        if (p.atMs > atterrissageMs(b) + PLACEMENT_MARGE_HAUT_MS)
            return "pose_apres_le_sol";
    }
    return null;
}
/**
 * **Le barème** : `score = 100 × (blocs bien rangés + erreurs pardonnées) / blocs`.
 *
 *  - un bloc ordinaire est bien rangé s'il s'est posé dans sa colonne ;
 *  - un **piège** est bien rangé s'il n'a pas été touché (le laisser filer) ;
 *  - un bloc qu'on n'a pas touché est tombé là où il est apparu : toujours raté pour un bloc
 *    ordinaire (le générateur ne l'y fait pas apparaître), sauf bloc `offert` ;
 *  - *Plume d'or* rattrape jusqu'à `erreursPardonnees` blocs ratés.
 *
 * Le score est un entier de 0 à 100. Pure et totale : un journal incohérent (bloc inconnu, colonne
 * hors plateau) est ignoré ligne à ligne, jamais une exception. Le rejeter est le travail de
 * `verifierPlacements`, appelé avant.
 */
export function noterBlocs(blocs, colonnes, placements, erreursPardonnees = 0) {
    const parBloc = new Map();
    for (const p of placements) {
        if (blocs[p.bloc] && colonnes.includes(p.colonne))
            parBloc.set(p.bloc, p);
    }
    const sorts = [];
    let bonnes = 0;
    let pieges = 0;
    let piegesEvites = 0;
    for (const b of blocs) {
        const p = parBloc.get(b.i);
        const touche = p !== undefined;
        const posee = p ? p.colonne : colonnes[b.voie];
        const piege = b.colonne === null;
        const ok = piege ? !touche : posee === b.colonne;
        if (piege) {
            pieges++;
            if (ok)
                piegesEvites++;
        }
        if (ok)
            bonnes++;
        sorts.push({ bloc: b.i, attendue: b.colonne, posee, touche, ok });
    }
    const total = blocs.length;
    const pardons = Math.min(Math.max(0, Math.trunc(erreursPardonnees)), total - bonnes);
    const score = total > 0 ? Math.round((100 * (bonnes + pardons)) / total) : 0;
    return { score: borne(score, 0, 100), bonnes, pardons, total, sorts, pieges, piegesEvites };
}
//# sourceMappingURL=blocs.js.map